import styled from 'styled-components';
import { darken } from 'polished';

/* ===== Identidade visual (alinhada ao login / Header) =====
   --primary: #ff6d3c (gradiente)   --blue: #1789dd
   --ink: #17263b / #2c405a   --muted: #64748b / #79869e
   --border: rgba(216, 224, 235, 0.9)

   Carrinho de solicitação (sem valores) com cara de e-commerce:
   cabeçalho de resumo, itens em cards numerados, stepper de quantidade,
   barra de ação (checkout) fixa e estado vazio acolhedor.
   A estrutura de <table>/RowMaster do index.js é preservada; cada linha
   vira um card via flexbox.
=========================================================== */

const PRIMARY_GRADIENT =
  'linear-gradient(135deg, #ff6d3c 0%, #ff875f 52%, #ff9b78 100%)';

export const Container = styled.div``;

export const Wrapper = styled.div`
  max-width: 980px;
  margin: 0 auto;
  padding: 4px 0 24px;
`;

Wrapper.NotFound = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  min-height: 280px;
  margin-top: 16px;
  padding: 48px 20px;
  text-align: center;

  background: #fff;
  border: 1px dashed rgba(216, 224, 235, 0.9);
  border-radius: 18px;

  > svg {
    color: rgba(255, 109, 60, 0.55);
  }

  > strong {
    font-size: 17px;
    font-weight: 800;
    color: #17263b;
  }

  > span {
    max-width: 360px;
    font-size: 13px;
    color: #79869e;
  }
`;

Wrapper.BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  padding: 11px 18px;

  border: 1px solid rgba(216, 224, 235, 0.9);
  border-radius: 12px;
  background: #fff;
  color: #2c405a;
  font-weight: 700;
  font-size: 13px;
  cursor: pointer;
  transition: color 0.2s ease, background 0.2s ease, border-color 0.2s ease,
    transform 0.2s ease;

  :hover {
    color: #ff6d3c;
    background: rgba(255, 109, 60, 0.06);
    border-color: rgba(255, 109, 60, 0.4);
    transform: translateY(-1px);
  }
`;

Wrapper.BtnImg = styled.div`
  position: relative;
  cursor: pointer;
  display: inline-flex;
  border-radius: 12px;
  transition: transform 0.2s ease;

  :hover {
    transform: scale(1.02);
  }
`;

/* ===== Cabeçalho / resumo da solicitação ===== */
export const Cart = styled.div``;

Cart.Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 14px;

  margin: 4px 0 18px;
  padding: 18px 20px;

  background: #fff;
  border: 1px solid rgba(216, 224, 235, 0.9);
  border-left: 4px solid #ff6d3c;
  border-radius: 16px;
  box-shadow: 0 6px 18px rgba(23, 38, 59, 0.05);
`;

Cart.HeaderTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  > strong {
    font-size: 18px;
    font-weight: 800;
    letter-spacing: -0.3px;
    color: #17263b;
  }

  > span {
    font-size: 13px;
    color: #79869e;
  }
`;

Cart.HeaderMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

Cart.Count = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: 999px;
  background: ${PRIMARY_GRADIENT};
  color: #fff;
  font-size: 13px;
  font-weight: 800;
  box-shadow: 0 8px 18px rgba(255, 109, 60, 0.3);
`;

Cart.Tag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 999px;
  background: #eef2f8;
  color: #64748b;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

Cart.ItemNo = styled.span`
  position: absolute;
  top: -8px;
  left: -8px;
  z-index: 1;

  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 6px;

  border-radius: 999px;
  background: ${PRIMARY_GRADIENT};
  color: #fff;
  font-size: 12px;
  font-weight: 800;
  box-shadow: 0 4px 10px rgba(255, 109, 60, 0.4);
`;

/* ===== Lista de itens (cada <tr> vira um card) ===== */
export const Table = styled.table`
  display: block;
  width: 100%;
  padding: 4px 0;
  font-size: 14px;

  > thead {
    display: none;
  }

  > tbody {
    display: block;
    width: 100%;
    user-select: text !important;

    > tr {
      display: flex;
      align-items: flex-start;
      gap: 18px;
      height: auto;

      background: #fff;
      border: 1px solid rgba(216, 224, 235, 0.9);
      border-radius: 16px;
      box-shadow: 0 6px 18px rgba(23, 38, 59, 0.06);
      padding: 16px 18px;
      margin-bottom: 16px;

      transition: border-color 0.2s ease, box-shadow 0.2s ease;

      :hover {
        background: #fff;
        border-color: rgba(255, 109, 60, 0.35);
        box-shadow: 0 14px 30px rgba(23, 38, 59, 0.12);
      }

      /* reset das bordas/padding que o RowMaster aplica nos td */
      > td {
        display: flex;
        padding: 0 !important;
        border: 0 !important;
        border-radius: 0 !important;
        background: transparent;
        color: #2c405a;
        vertical-align: top;

        button {
          border: 0;
          background: none;
        }
      }

      /* imagem (thumbnail) */
      > td:nth-child(1) {
        flex: 0 0 auto;

        img {
          width: 120px;
          height: 120px;
          object-fit: contain;
          background: #f8fafc;
          border: 1px solid rgba(216, 224, 235, 0.9);
          border-radius: 12px;
          padding: 6px;
          box-shadow: none;
        }
      }

      /* partnumber (SKU) */
      > td:nth-child(2) {
        flex: 0 0 auto;
        min-width: 120px;
        max-width: 180px;
        flex-direction: column;

        > strong {
          display: inline-block;
          padding: 5px 10px;
          border-radius: 8px;
          background: rgba(255, 109, 60, 0.1);
          color: #d4572a;
          font-size: 13px;
          font-weight: 800;
          letter-spacing: 0.3px;
          word-break: break-all;
        }
      }

      /* descrição + detalhes (área principal) */
      > td:nth-child(3) {
        flex: 1 1 auto;
        min-width: 0;
      }

      /* quantidade */
      > td:nth-child(4) {
        flex: 0 0 auto;
        align-items: center;
        align-self: center;
      }

      /* remover */
      > td:nth-child(5) {
        flex: 0 0 auto;
      }
    }
  }

  @media (max-width: 760px) {
    > tbody > tr {
      flex-wrap: wrap;

      > td:nth-child(3) {
        flex: 1 1 100%;
        order: 5;
      }

      > td:nth-child(4) {
        margin-left: auto;
      }
    }
  }
`;

Table.Description = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  > strong {
    font-size: 16px;
    font-weight: 800;
    letter-spacing: -0.2px;
    color: #17263b;
  }

  > span {
    color: #64748b;
    font-size: 13px;
  }
`;

Table.Combo = styled.div`
  display: flex;
  align-items: center;
  margin-top: 10px;
  color: #2c405a;
  font-size: 13px;
  font-weight: 600;

  > div {
    padding: 0;
    margin-left: 8px;
  }
`;

Table.Error = styled.span`
  display: inline-block;
  width: fit-content;
  margin-top: 6px;
  padding: 6px 10px;
  border-radius: 8px;
  background: rgba(222, 59, 59, 0.08);
  color: #dd0312;
  font-style: normal;
  font-size: 12px;
  font-weight: 600;
`;

Table.Quantity = styled.div`
  display: inline-flex;
  align-items: stretch;
  height: 40px;
  border: 1px solid rgba(216, 224, 235, 0.9);
  border-radius: 10px;
  overflow: hidden;
  background: #f8fafc;

  > button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 100%;
    color: #2c405a;
    cursor: pointer;
    transition: background 0.18s ease, color 0.18s ease;

    :hover {
      background: rgba(255, 109, 60, 0.12);
      color: #ff6d3c;
    }
  }

  span {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 46px;
    height: 100%;
    padding: 0;
    font-weight: 800;
    color: #17263b;
    background: #fff;
    border-left: 1px solid rgba(216, 224, 235, 0.9);
    border-right: 1px solid rgba(216, 224, 235, 0.9);
  }
`;

Table.Action = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

Table.BtnRemove = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  color: #8fa0ba;
  cursor: pointer;
  transition: color 0.2s ease, background 0.2s ease, transform 0.2s ease;

  :hover {
    color: ${darken(0.05, '#de3b3b')};
    background: rgba(222, 59, 59, 0.12);
    transform: translateY(-1px);
  }
`;

/* ===== Bloco "Outras Informações" como painel/resumo ===== */
export const Section = styled.div`
  margin: 6px 0 18px;
  padding: 20px 22px;
  background: #fff;
  border: 1px solid rgba(216, 224, 235, 0.9);
  border-radius: 16px;
  box-shadow: 0 6px 18px rgba(23, 38, 59, 0.05);
`;

/* ===== Barra de ação (checkout) ===== */
export const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  padding: 14px 18px;
  margin-bottom: 10px;

  background: #fff;
  border: 1px solid rgba(216, 224, 235, 0.9);
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(23, 38, 59, 0.08);

  position: sticky;
  bottom: 10px;
  z-index: 2;

  > button {
    border: 0;
    border-radius: 12px;
    color: #fff;
    height: 46px;
    min-width: 220px;
    font-weight: 800;
  }

  #btn-action {
    border: 0;
    border-radius: 12px;
    color: #fff;
    height: 46px;
    min-width: 220px;
    background: #2c405a;
    transition: background 0.2s ease, transform 0.2s ease;

    :hover {
      color: #fff;
      background: ${darken(0.06, '#2c405a')};
      transform: translateY(-1px);
    }
  }
`;

Footer.Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

Footer.Count = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 700;
  color: #64748b;
  white-space: nowrap;
`;

Footer.Export = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 46px;
  min-width: 220px;
  padding: 0 22px;
  border: 0;
  border-radius: 12px;
  color: #fff;
  font-weight: 800;
  cursor: pointer;

  background: #2c405a;
  transition: background 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;

  :hover {
    background: ${darken(0.06, '#2c405a')};
    transform: translateY(-1px);
    box-shadow: 0 12px 26px rgba(44, 64, 90, 0.28);
  }
`;

Footer.Submit = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 46px;
  min-width: 220px;
  padding: 0 22px;
  border: 0;
  border-radius: 12px;
  color: #fff;
  font-weight: 800;
  cursor: pointer;

  background: ${PRIMARY_GRADIENT};
  box-shadow: 0 12px 26px rgba(255, 109, 60, 0.3);
  transition: transform 0.2s ease, box-shadow 0.2s ease, filter 0.2s ease;

  :hover {
    transform: translateY(-1px);
    box-shadow: 0 16px 34px rgba(255, 109, 60, 0.44);
    filter: brightness(1.02);
  }

  :active {
    transform: translateY(0);
  }
`;

/* ===== Sub-painel de informações de O.S. (dentro do card) ===== */
export const WrapperItemOS = styled.div`
  margin-top: 10px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #f8fafc;
  border: 1px solid rgba(216, 224, 235, 0.9);

  & + & {
    margin-top: 8px;
  }
`;

export const ItemOSTitle = styled.p`
  padding-bottom: 6px;
  margin-bottom: 8px;
  color: #17263b;
  font-weight: 800;
  font-size: 12px;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  border-bottom: 1px solid rgba(216, 224, 235, 0.9);
`;

export const WrapperTableItemOS = styled.div`
  display: flex;
  flex-direction: row;
`;

export const ItemOSTableLabel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  min-width: 120px;

  > span {
    padding: 2px 5px 2px 0;
    color: #374151;
    font-weight: 600;
  }
`;

export const ItemOSTableContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  padding-left: 5px;

  > p {
    padding: 2px 0;
    color: #6b7280;
  }
`;
